import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { Chamado } from './chamadoSocorristaList';
import MapPatner from './MapPatner';
// Removido import getAddressFromCoords - usando endereco_textual do banco
import { useNavigate } from 'react-router-dom';

interface ChamadoModalProps {
  open: boolean;
  chamado: Chamado | null;
  onClose: () => void;
  onFazerAtendimento: () => void;
}

const ChamadoModal: React.FC<ChamadoModalProps> = ({ open, chamado, onClose, onFazerAtendimento }) => {
  const [distancia, setDistancia] = React.useState<string>('');
  const [estimativaTempo, setEstimativaTempo] = React.useState<string>('');
  const [nomeCliente, setNomeCliente] = useState<string>('');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [routeCoords, setRouteCoords] = useState<{ lat: number; lng: number }[]>([]);
  const [routeDuration, setRouteDuration] = useState<number | null>(null);
  const navigate = useNavigate();

  // Endereço agora vem diretamente do campo endereco_textual do banco
  React.useEffect(() => {
    if (chamado) {
      setDistancia('');
      setEstimativaTempo('');
    }
  }, [chamado]);

  useEffect(() => {
    setNomeCliente('');
  }, [chamado]);

  React.useEffect(() => {
    let ignore = false;
    async function calcularDistanciaTempo() {
      if (chamado && chamado.localizacao) {
        const [lat, lon] = chamado.localizacao.split(',').map(Number);
        let userLat: number | null = null;
        let userLon: number | null = null;
        if (navigator.geolocation) {
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
            });
            userLat = position.coords.latitude;
            userLon = position.coords.longitude;
          } catch (geoErr) {
            // Se der erro, deixa null
          }
        }
        if (userLat !== null && userLon !== null) {
          // Cálculo de distância
          const R = 6371; // km
          const dLat = (lat - userLat) * Math.PI / 180;
          const dLon = (lon - userLon) * Math.PI / 180;
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(userLat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          const distanciaKm = R * c;
          if (!ignore) {
            setDistancia(`${distanciaKm.toFixed(2)} km`);
            // Tempo estimado agora vem da rota real
            setEstimativaTempo(routeDuration !== null ? `${routeDuration} min` : '--');
          }
        } else {
          if (!ignore) {
            setDistancia('--');
            setEstimativaTempo('--');
          }
        }
      } else {
        if (!ignore) {
          setDistancia('--');
          setEstimativaTempo('--');
        }
      }
    }
    calcularDistanciaTempo();
    return () => { ignore = true; };
  }, [chamado, routeDuration]);

  useEffect(() => {
    let watchId: number;
    
    if (open) {
      if (navigator.geolocation) {
        // Configurações para maior precisão
        const options = {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        };

        watchId = navigator.geolocation.watchPosition(
          (position) => {
            setUserLocation({ 
              lat: position.coords.latitude, 
              lng: position.coords.longitude 
            });
            console.log('Localização atualizada:', position.coords.latitude, position.coords.longitude);
          },
          (error) => {
            console.error('Erro ao obter localização:', error);
            setUserLocation(null);
          },
          options
        );
      } else {
        setUserLocation(null);
      }
    }

    // Cleanup: remove o watch quando o componente é desmontado
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [open]);

  useEffect(() => {
    let isSubscribed = true;

    const fetchRoute = async () => {
      if (userLocation && chamado && chamado.localizacao) {
        const [destinoLat, destinoLng] = chamado.localizacao.split(',').map(Number);
        // Corrigindo a ordem das coordenadas: origem -> destino
        const url = `https://router.project-osrm.org/route/v1/driving/${userLocation.lng},${userLocation.lat};${destinoLng},${destinoLat}?overview=full&geometries=geojson&steps=true`;
        try {
          console.log('Buscando nova rota...', {
            origem: { lat: userLocation.lat, lng: userLocation.lng },
            destino: { lat: destinoLat, lng: destinoLng }
          });
          
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          
          if (data.routes && data.routes[0] && isSubscribed) {
            // Processando as coordenadas corretamente
            const newRouteCoords = data.routes[0].geometry.coordinates.map(
              ([lng, lat]: [number, number]) => ({ lat, lng })
            );
            const newDuration = Math.ceil(data.routes[0].duration / 60);
            
            console.log('Rota obtida com sucesso:', {
              numPontos: newRouteCoords.length,
              primeiroPonto: newRouteCoords[0],
              ultimoPonto: newRouteCoords[newRouteCoords.length - 1]
            });
            
            setRouteCoords(newRouteCoords);
            setRouteDuration(newDuration);
            console.log('Rota atualizada. Duração:', newDuration, 'minutos');
          } else {
            console.warn('Nenhuma rota encontrada nos dados:', data);
            setRouteCoords([]);
            setRouteDuration(null);
          }
        } catch (error) {
          console.error('Erro ao buscar rota:', error);
          setRouteCoords([]);
          setRouteDuration(null);
        }
      } else {
        if (!userLocation) console.log('Aguardando localização do usuário...');
        if (!chamado) console.log('Chamado não disponível...');
        if (!chamado?.localizacao) console.log('Localização do chamado não disponível...');
        setRouteCoords([]);
        setRouteDuration(null);
      }
    };

    fetchRoute();

    return () => {
      isSubscribed = false;
    };
  }, [userLocation, chamado]);

  function formatarDataHora(data: string) {
    if (!data) return '';
    const d = new Date(data);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const ano = d.getFullYear();
    const hora = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${hora}:${min}`;
  }

  const handleFazerAtendimento = async () => {
    if (!chamado) return;
    try {
      const url = process.env.REACT_APP_SUPABASE_URL;
      const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
      const accessToken = localStorage.getItem('accessToken');
      if (!url || !serviceKey) throw new Error('Supabase URL ou Service Key não definidos');
      if (!accessToken) throw new Error('Token de acesso não encontrado no localStorage');

      let posicao = '';
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
          });
          posicao = `${position.coords.latitude},${position.coords.longitude}`;
        } catch (geoErr) {
          // Se der erro, mantém vazio
        }
      }

      const payload = { 
        status: 'A caminho', 
        socorrista_id: localStorage.getItem('socorristaId'),
        posicao_inicial_socorrista: posicao
      };
      console.log('[ChamadoModal] Enviando PATCH para /chamado:', payload);
      const response = await fetch(`${url}/rest/v1/chamado?id=eq.${chamado.id}`, {
        method: 'PATCH',
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error_description || data.message || `Erro ${response.status}`);
      }
      // Criar log_chamado após aceitar
      const chamadoResp = await fetch(`${url}/rest/v1/chamado?id=eq.${chamado.id}`, {
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      const chamadoData = await chamadoResp.json();
      if (chamadoData && chamadoData[0]) {
        const logData = {
          chamado_id: chamadoData[0].id,
          cliente_id: chamadoData[0].cliente_id,
          localizacao: chamadoData[0].localizacao,
          endereco_textual: chamadoData[0].endereco_textual,
          status: chamadoData[0].status,
          operador_id: chamadoData[0].operador_id,
          socorrista_id: chamadoData[0].socorrista_id,
          data_abertura: chamadoData[0].data_abertura,
          data_fechamento: chamadoData[0].data_fechamento,
          descricao: chamadoData[0].descricao,
          prioridade: chamadoData[0].prioridade,
          notificacao_familiares: chamadoData[0].notificacao_familiares,
          posicao_inicial_socorrista: chamadoData[0].posicao_inicial_socorrista
        };
        await fetch(`${url}/rest/v1/log_chamado`, {
          method: 'POST',
          headers: {
            'apikey': serviceKey,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(logData)
        });
      }
      // Salva o id do chamado no localStorage
      localStorage.setItem('chamadoId', chamado.id);
      onFazerAtendimento();
      navigate('/emergencia-socorrista');
    } catch (err) {
      alert('Erro ao atualizar status para A caminho.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalhes do Chamado</DialogTitle>
      <DialogContent sx={{ p: 2, pb: 0, position: 'relative' }}>
        <Box
          component="img"
          src={require('../../assets/Design sem nome (7).png')}
          alt="Marca d'água Support Life"
          sx={{
            position: 'absolute',
            top: '-120px',
            left: '50%',
            width: '100%',
            height: '360px',
            objectFit: 'contain',
            opacity: 0.07,
            pointerEvents: 'none',
            zIndex: 0,
            transform: 'translateX(-50%)',
          }}
        />
        {/* Conteúdo principal */}
        {chamado && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, position: 'relative', zIndex: 1, mt: 0 }}>
            <Typography sx={{ mt: 0, mb: 0 }}><strong>Nº Chamado:</strong> {chamado.id}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 0.25, mb: 0.25 }}>
              <Box sx={{ flex: 1, textAlign: 'left' }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 0 }}>
                  {distancia || '--'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0 }}>
                  Distância
                </Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: 'right' }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 0 }}>
                  {estimativaTempo || '--'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0 }}>
                  Tempo estimado
                </Typography>
              </Box>
            </Box>
            {chamado.localizacao && (() => {
              const parts = chamado.localizacao.split(',');
              if (parts.length !== 2) return null;
              
              const lat = Number(parts[0]);
              const lng = Number(parts[1]);
              
              if (isNaN(lat) || isNaN(lng)) return null;

              return (
                <Box sx={{ mt: 0.25 }}>
                  <MapPatner
                    center={{ lat, lng }}
                    zoom={15}
                    markers={[
                      {
                        position: { lat, lng },
                        title: 'Local do chamado'
                      }
                    ]}
                    routeCoords={routeCoords}
                    ambulancePosition={userLocation || undefined}
                  />
                  <Typography sx={{ fontSize: 15, color: 'text.secondary', mb: 3, mt: 3 }}>
                    <strong>Endereço:</strong> {chamado.endereco_textual || 'Endereço não disponível'}
                  </Typography>
                </Box>
              );
            })()}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        width: '100%',
        justifyContent: 'space-between',
        pb: 2,
        pt: 0,
        px: 3
      }}>
        <Button
          variant="contained"
          color="primary"
          sx={{ minWidth: 120, fontWeight: 600 }}
          fullWidth
          onClick={handleFazerAtendimento}
        >
          Fazer Atendimento
        </Button>
        <Button
          variant="outlined"
          color="error"
          sx={{ minWidth: 120, fontWeight: 600 }}
          fullWidth
          onClick={onClose}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChamadoModal; 
